import React, { useState } from 'react';
import { MessageCircle, CornerDownRight, Edit2, Trash2, Send, X, Loader2 } from 'lucide-react';
import { useReviewContext } from '../context/ReviewContext';
import { toast } from 'react-toastify';

const ReviewReplySection = ({ review, tokenStorageKey }) => {
  const { replyToReview, removeReply } = useReviewContext();
  const [isReplying, setIsReplying] = useState(false);
  const [replyText, setReplyText] = useState(review?.reply?.comment || '');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem(tokenStorageKey);

  const handleOpenReply = () => {
    setReplyText(review?.reply?.comment || '');
    setIsReplying(true);
  };

  const handleCancel = () => {
    setIsReplying(false);
    setReplyText(review?.reply?.comment || '');
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error('Reply content cannot be empty.');
      return;
    }

    setSubmitting(true);
    const result = await replyToReview(review._id, replyText.trim(), token);
    setSubmitting(false);

    if (result.success) {
      toast.success(review?.reply ? 'Reply updated successfully!' : 'Reply posted successfully!');
      setIsReplying(false);
    } else {
      toast.error(result.message || 'Failed to submit reply.');
    }
  };

  const handleDeleteReply = async () => {
    if (!window.confirm('Are you sure you want to delete your response?')) return;

    setDeleting(true);
    const result = await removeReply(review._id, token);
    setDeleting(false);

    if (result.success) {
      toast.success('Reply deleted.');
      setReplyText('');
      setIsReplying(false);
    } else {
      toast.error(result.message || 'Failed to delete reply.');
    }
  };

  const replyComment = typeof review?.reply === 'object' && review?.reply !== null
    ? (review.reply.comment || '')
    : (typeof review?.reply === 'string' ? review.reply : '');

  const hasReply = Boolean(replyComment && replyComment.trim().length > 0);

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      {/* Existing Reply View */}
      {hasReply && !isReplying && (
        <div className="bg-primary/5 border border-primary/15 rounded-2xl p-4 relative group/reply">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CornerDownRight className="w-4 h-4 text-primary" />
              <span className="text-xs font-black text-primary uppercase tracking-wider">
                Partner Response
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-gray-400">
                {review.reply?.createdAt ? new Date(review.reply.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                }) : ''}
              </span>
              <button
                onClick={handleOpenReply}
                title="Edit response"
                className="p-1 hover:bg-primary/10 rounded-lg text-gray-400 hover:text-primary transition-all"
              >
                <Edit2 size={13} />
              </button>
              <button
                onClick={handleDeleteReply}
                disabled={deleting}
                title="Delete response"
                className="p-1 hover:bg-rose-50 rounded-lg text-gray-400 hover:text-rose-500 transition-all"
              >
                {deleting ? <Loader2 size={13} className="animate-spin text-rose-500" /> : <Trash2 size={13} />}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-700 font-medium leading-relaxed italic">
            "{replyComment}"
          </p>
        </div>
      )}

      {/* Reply Form */}
      {isReplying && (
        <form onSubmit={handleSubmitReply} className="space-y-3 bg-gray-50/80 p-4 rounded-2xl border border-gray-200/60 animate-in fade-in duration-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
              <CornerDownRight className="w-3.5 h-3.5 text-primary" />
              {hasReply ? 'Edit Response' : 'Reply to Guest'}
            </span>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X size={14} />
            </button>
          </div>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a polite, professional response to your guest..."
            rows={3}
            maxLength={500}
            className="w-full text-xs p-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-medium text-gray-800"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400 font-semibold">
              {replyText.length}/500
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:bg-gray-200/60 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !replyText.trim()}
                className="px-4 py-1.5 bg-primary text-white text-xs font-bold rounded-xl shadow-md shadow-primary/20 hover:bg-primary/90 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <>
                    <Send size={13} />
                    {hasReply ? 'Update' : 'Post Reply'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* No Reply Button */}
      {!hasReply && !isReplying && (
        <button
          onClick={handleOpenReply}
          className="flex items-center gap-2 text-xs font-bold text-primary hover:text-primary/80 transition-all hover:translate-x-0.5 py-1"
        >
          <MessageCircle size={14} />
          <span>Reply to Guest</span>
        </button>
      )}
    </div>
  );
};

export default ReviewReplySection;
